import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  MessageSquare,
  TrendingUp,
  Shield,
  Plus,
  Trash2,
  Pencil,
  X,
  Loader2,
  Building2,
  Send,
} from 'lucide-react';
import { api } from '../../utils/api';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    company_name: '',
    sheet_url: '',
    is_admin: false,
  });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: '',
    company_name: '',
    sheet_url: '',
    is_admin: false,
    is_super_admin: false,
  });
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  // Business management state
  const [editBusinesses, setEditBusinesses] = useState([]);
  const [newBusinessName, setNewBusinessName] = useState('');
  const [newBusinessSheet, setNewBusinessSheet] = useState('');
  const [editingBizId, setEditingBizId] = useState(null);
  const [editBizForm, setEditBizForm] = useState({ name: '', sheet_url: '' });
  const [sendingWelcome, setSendingWelcome] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [statsData, usersData, convsData] = await Promise.all([
        api.getAdminStats(),
        api.getAllUsers(),
        api.getAllConversations(),
      ]);
      setStats(statsData);
      setUsers(usersData);
      setConversations(convsData);
    } catch (err) {
      setError(err.message || 'Failed to load admin data');
      if (err.message?.includes('Admin access denied')) {
        setError('Admin access required. Redirecting to dashboard.');
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.createUser({
        email: newUser.email,
        full_name: newUser.full_name,
        company_name: newUser.company_name || null,
        sheet_url: newUser.sheet_url || null,
        is_admin: newUser.is_admin,
      });
      setShowCreateUser(false);
      setNewUser({
        email: '',
        full_name: '',
        company_name: '',
        sheet_url: '',
        is_admin: false,
      });
      loadData();
      setSuccessMsg('User created. Setup email sent.');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure? This will delete all their conversations.')) return;
    try {
      await api.deleteUser(userId);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      full_name: user.full_name || '',
      company_name: user.company_name || '',
      sheet_url: user.sheet_url || '',
      is_admin: user.is_admin,
      is_super_admin: user.is_super_admin || false,
    });
    setEditBusinesses(user.businesses || []);
    setNewBusinessName('');
    setNewBusinessSheet('');
    setEditingBizId(null);
  };

  const handleAddBusiness = async () => {
    if (!newBusinessName.trim()) return;
    setError('');
    try {
      const biz = await api.addBusiness(editingUser.id, {
        name: newBusinessName,
        sheet_url: newBusinessSheet || null,
      });
      setEditBusinesses([...editBusinesses, biz]);
      setNewBusinessName('');
      setNewBusinessSheet('');
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to add business');
    }
  };

  const handleUpdateBusiness = async (bizId) => {
    setError('');
    try {
      const updated = await api.updateBusiness(bizId, editBizForm);
      setEditBusinesses(editBusinesses.map(b => b.id === bizId ? updated : b));
      setEditingBizId(null);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to update business');
    }
  };

  const handleDeleteBusiness = async (bizId) => {
    if (!window.confirm('Delete this business? Conversations linked to it will be unlinked.')) return;
    setError('');
    try {
      await api.deleteBusiness(bizId);
      setEditBusinesses(editBusinesses.filter(b => b.id !== bizId));
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete business');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.updateUser(editingUser.id, editForm);
      setEditingUser(null);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to update user');
    }
  };

  const handleSendWelcome = async (userId) => {
    setSendingWelcome(userId);
    setError('');
    try {
      const result = await api.sendWelcome(userId);
      setSuccessMsg(result.message || 'Welcome message sent!');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to send welcome message');
    } finally {
      setSendingWelcome(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-header">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} />
            Back to dashboard
          </button>
          <div className="admin-title">
            <Shield size={20} />
            <h1>Admin panel</h1>
          </div>
        </div>
        <div className="admin-loading">
          <Loader2 size={24} className="admin-spinner" />
          <span>Loading admin data…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} />
          Back to dashboard
        </button>
        <div className="admin-title">
          <Shield size={20} />
          <h1>Admin panel</h1>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {successMsg && <div className="admin-success">{successMsg}</div>}

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <TrendingUp size={16} />
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={16} />
          Users
        </button>
        <button
          className={`tab ${activeTab === 'conversations' ? 'active' : ''}`}
          onClick={() => setActiveTab('conversations')}
        >
          <MessageSquare size={16} />
          Conversations
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'stats' && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div>
                <div className="stat-value">{stats?.user_count || 0}</div>
                <div className="stat-label">Total users</div>
                <div className="stat-breakdown">{stats?.admin_count || 0} admins</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🟢</div>
              <div>
                <div className="stat-value">{stats?.active_user_count || 0}</div>
                <div className="stat-label">Active now</div>
                <div className="stat-breakdown">logged in within 30 min</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💬</div>
              <div>
                <div className="stat-value">{stats?.conversation_count?.toLocaleString() || 0}</div>
                <div className="stat-label">Conversations</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔢</div>
              <div>
                <div className="stat-value">{stats?.total_tokens?.toLocaleString() || 0}</div>
                <div className="stat-label">Total tokens</div>
                <div className="stat-breakdown">
                  ↑{stats?.total_input_tokens?.toLocaleString() || 0} / ↓{stats?.total_output_tokens?.toLocaleString() || 0}
                </div>
              </div>
            </div>
            <div className="stat-card cost-card">
              <div className="stat-icon">💸</div>
              <div>
                <div className="stat-value">${stats?.total_cost?.toFixed(4) || '0.0000'}</div>
                <div className="stat-label">Total API cost</div>
                <div className="stat-breakdown">Claude + Twilio</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-header">
              <h2>Users</h2>
              <button className="btn-primary" onClick={() => setShowCreateUser(true)}>
                <Plus size={16} />
                New user
              </button>
            </div>
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Businesses</th>
                    <th>Role</th>
                    <th>Tokens</th>
                    <th>Cost</th>
                    <th>Last WA In</th>
                    <th>Last WA Out</th>
                    <th>Created</th>
                    <th>Last login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div>{user.full_name || '—'}</div>
                      </td>
                      <td>
                        <div className="business-count">
                          <Building2 size={14} />
                          <span>{user.businesses?.length || 0}</span>
                        </div>
                        {user.businesses?.length > 0 && (
                          <div className="business-names-preview">
                            {user.businesses.map(b => b.name).join(', ')}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={user.is_super_admin ? 'badge-super-admin' : user.is_admin ? 'badge-admin' : 'badge-user'}>
                          {user.is_super_admin ? 'Super' : user.is_admin ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td className="token-cell">
                        <div className="token-total">{user.total_tokens?.toLocaleString() || 0}</div>
                        <div className="token-breakdown">
                          ↑{user.input_tokens?.toLocaleString() || 0} / ↓{user.output_tokens?.toLocaleString() || 0}
                        </div>
                      </td>
                      <td className="cost-cell">${user.total_cost?.toFixed(4) || '0.0000'}</td>
                      <td className="wa-msg-cell">
                        {user.wa_last_incoming_at ? (
                          <div title={user.wa_last_incoming || ''}>
                            <div className="wa-msg-time">{new Date(user.wa_last_incoming_at).toLocaleString('en-CA', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                            <div className="wa-msg-preview">{user.wa_last_incoming?.slice(0, 40) || '—'}{user.wa_last_incoming?.length > 40 ? '…' : ''}</div>
                          </div>
                        ) : '—'}
                      </td>
                      <td className="wa-msg-cell">
                        {user.wa_last_outgoing_at ? (
                          <div title={user.wa_last_outgoing || ''}>
                            <div className="wa-msg-time">{new Date(user.wa_last_outgoing_at).toLocaleString('en-CA', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                            <div className="wa-msg-preview">{user.wa_last_outgoing?.slice(0, 40) || '—'}{user.wa_last_outgoing?.length > 40 ? '…' : ''}</div>
                          </div>
                        ) : '—'}
                      </td>
                      <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</td>
                      <td>
                        {user.last_login
                          ? new Date(user.last_login).toLocaleString('en-CA', {
                              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                      <td className="actions-cell">
                        <button className="btn-edit" onClick={() => handleEditUser(user)} title="Edit user" aria-label={`Edit user ${user.email}`}>
                          <Pencil size={14} />
                        </button>
                        {user.phone_number && (
                          <button
                            className="btn-send"
                            onClick={() => handleSendWelcome(user.id)}
                            title="Send welcome WhatsApp"
                            aria-label={`Send welcome to ${user.email}`}
                            disabled={sendingWelcome === user.id}
                          >
                            {sendingWelcome === user.id ? <Loader2 size={14} className="admin-spinner" /> : <Send size={14} />}
                          </button>
                        )}
                        <button className="btn-delete" onClick={() => handleDeleteUser(user.id)} title="Delete user" aria-label={`Delete user ${user.email}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={10} className="empty-state-cell">
                        <Users size={32} style={{ opacity: 0.3 }} />
                        <p>No users yet. Click "New user" to add your first team member.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'conversations' && (
          <div className="conversations-section">
            <div className="section-header">
              <h2>Conversations</h2>
            </div>
            <div className="conversations-table">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Title</th>
                    <th>Tokens</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map((conv) => (
                    <tr key={conv.id}>
                      <td>{conv.user_email}</td>
                      <td>{conv.title}</td>
                      <td>{conv.total_tokens?.toLocaleString() || 0}</td>
                      <td>{conv.created_at ? new Date(conv.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                  {conversations.length === 0 && (
                    <tr>
                      <td colSpan={4} className="empty-state-cell">
                        <MessageSquare size={32} style={{ opacity: 0.3 }} />
                        <p>No conversations yet. Users will see their chat history here.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showCreateUser && (
        <div className="modal-overlay" onClick={() => setShowCreateUser(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create new user</h3>
              <button onClick={() => setShowCreateUser(false)}>
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Full name</label>
                <input
                  type="text"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Company name</label>
                <input
                  type="text"
                  value={newUser.company_name}
                  onChange={(e) => setNewUser({ ...newUser, company_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Google Sheet URL (optional)</label>
                <input
                  type="text"
                  value={newUser.sheet_url}
                  onChange={(e) => setNewUser({ ...newUser, sheet_url: e.target.value })}
                />
              </div>
              <div className="form-group-checkbox">
                <input
                  type="checkbox"
                  checked={newUser.is_admin}
                  onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })}
                />
                <label>Admin user</label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateUser(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create user
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit user</h3>
              <button onClick={() => setEditingUser(null)}>
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={editingUser.email} disabled className="input-disabled" />
              </div>
              {editingUser.phone_number && (
                <div className="form-group">
                  <label>WhatsApp</label>
                  <div className="phone-display">
                    <MessageSquare size={14} />
                    <span>{editingUser.phone_number}</span>
                    {editingUser.phone_verified && <span className="badge-verified">Verified</span>}
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Full name</label>
                <input
                  type="text"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Company name</label>
                <input
                  type="text"
                  value={editForm.company_name}
                  onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                />
              </div>
              <div className="form-group-checkbox">
                <input
                  type="checkbox"
                  checked={editForm.is_admin}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEditForm({ ...editForm, is_admin: checked, is_super_admin: checked ? editForm.is_super_admin : false });
                  }}
                />
                <label>Admin user</label>
              </div>
              {editForm.is_admin && (
                <div className="form-group-checkbox super-admin-toggle">
                  <input
                    type="checkbox"
                    checked={editForm.is_super_admin}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (checked) {
                        const confirmed = window.confirm(
                          'Are you sure you want to grant Super Admin access?\n\n' +
                          'Super Admins can view financial data from ALL businesses across ALL users.'
                        );
                        if (!confirmed) return;
                      }
                      setEditForm({ ...editForm, is_super_admin: checked });
                    }}
                  />
                  <label>Super admin</label>
                  <span className="super-admin-hint">Can view all businesses across all users</span>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditingUser(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save changes
                </button>
              </div>
            </form>

            {/* Businesses Section */}
            <div className="businesses-section">
              <h4><Building2 size={16} /> Businesses & Data Sources</h4>
              {editBusinesses.length === 0 && (
                <p className="no-businesses-text">No businesses yet. Add one below.</p>
              )}
              <div className="business-list">
                {editBusinesses.map(biz => (
                  <div key={biz.id} className="business-row">
                    {editingBizId === biz.id ? (
                      <div className="business-edit-inline">
                        <input
                          type="text"
                          value={editBizForm.name}
                          onChange={(e) => setEditBizForm({ ...editBizForm, name: e.target.value })}
                          placeholder="Business name"
                        />
                        <input
                          type="text"
                          value={editBizForm.sheet_url}
                          onChange={(e) => setEditBizForm({ ...editBizForm, sheet_url: e.target.value })}
                          placeholder="Google Sheet URL"
                        />
                        <div className="business-edit-actions">
                          <button type="button" className="btn-sm-primary" onClick={() => handleUpdateBusiness(biz.id)}>Save</button>
                          <button type="button" className="btn-sm-secondary" onClick={() => setEditingBizId(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="business-info">
                          <span className="business-name">
                            {biz.name}
                            {biz.is_default && <span className="badge-default">Default</span>}
                          </span>
                          <span className="business-sheet-url">{biz.sheet_url || 'No sheet connected'}</span>
                        </div>
                        <div className="business-actions">
                          <button
                            className="btn-edit"
                            onClick={() => { setEditingBizId(biz.id); setEditBizForm({ name: biz.name, sheet_url: biz.sheet_url || '' }); }}
                            title="Edit business"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteBusiness(biz.id)}
                            title="Delete business"
                            disabled={editBusinesses.length <= 1}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className="add-business-form">
                <div className="add-biz-row">
                  <input
                    type="text"
                    value={newBusinessName}
                    onChange={(e) => setNewBusinessName(e.target.value)}
                    placeholder="Business name"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBusiness(); } }}
                  />
                </div>
                <div className="add-biz-row">
                  <input
                    type="text"
                    value={newBusinessSheet}
                    onChange={(e) => setNewBusinessSheet(e.target.value)}
                    placeholder="Google Sheet URL (optional)"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBusiness(); } }}
                  />
                </div>
                <button type="button" className="btn-primary btn-sm" onClick={handleAddBusiness} disabled={!newBusinessName.trim()}>
                  <Plus size={14} /> Add Business
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
